import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCivilAppellateWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { CivilAppellateWizardDraft } from '../../../types/civilAppellateCaseRequest';
import * as civilAppellateService from '../../../services/civilAppellateCaseRequests';
import { WizardTempFileUploadList } from '../../../components/wizard/WizardTempFileUploadList';
import { sliceMax } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { CivilAppellateWizardActions } from './CivilAppellateWizardActions';
import { civilAppellateWizardPath, CivilAppellateWizardShell, FieldError, inpClass } from './CivilAppellateWizardShell';
import { useCivilAppellateLookups } from './useCivilAppellateLookups';
import { validateCivilAppellateStep } from './civilAppellateStepValidation';
import { useCaseCategorization } from './useCaseCategorization';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_DESC = 2000;
const MAX_REF = 255;

const toOpts = (items: { id: string; name: string }[]) => items.map((x) => ({ id: x.id, label: x.name }));

function lookupName(items: { id: string; name: string }[], id: string): string {
  return items.find((x) => x.id === id)?.name ?? '';
}

export default function CivilAppellateStep1CaseOverviewPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useCivilAppellateWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const cat = useCaseCategorization(draft.fileCategoryId, draft.subFileCategoryId);
  const lookups = useCivilAppellateLookups('step1', draft.provinceId);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  useEffect(() => {
    if (draft.hasConnectedMatters === 'yes' || draft.hasConnectedMatters === 'no') return;
    setDraft((d: CivilAppellateWizardDraft) => ({ ...d, hasConnectedMatters: 'no' }));
  }, [draft.hasConnectedMatters, setDraft]);

  useEffect(() => {
    if (draft.connectedAgdFileRef?.trim() && !draft.connectedAgdFileRefAvailable) {
      setDraft((d: CivilAppellateWizardDraft) => ({ ...d, connectedAgdFileRefAvailable: true }));
    }
  }, [draft.connectedAgdFileRef, draft.connectedAgdFileRefAvailable, setDraft]);


  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateCivilAppellateStep(
      1,
      draft,
      cat.fileCategories,
      lookups.corporateInstitutes,
    );
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const next = () => {
    if (!validate()) return;
    nav(civilAppellateWizardPath(2, draft.draftRequestId));
  };

  return (
    <CivilAppellateWizardShell
      currentStep={1}
      draft={draft}
      setDraft={setDraft}
      categorizationEditable
      categorizationErrors={errors}
      onClearCategorizationError={clearErr}
    >
      <SectionCard title={f.sectionCaseIntakeDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.description} <span className="req">*</span>
          </label>
          <textarea
            id="capl-description"
            className={inpClass(Boolean(errors.description))}
            rows={4}
            value={draft.description}
            onChange={(e) => {
              setDraft((d: CivilAppellateWizardDraft) => ({ ...d, description: maxLen('description', e.target.value, MAX_DESC) }));
            }}
            placeholder={f.briefDescriptionOfCase}
          />
          <FieldError message={errors.description} />
        </div>

        <div className="ccr-fg" id="capl-appealMatter" style={{ marginBottom: 14 }}>
          <label>
            {f.appealMatter} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="appealMatter"
                checked={draft.isAppealMatter === 'yes'}
                onChange={() => {
                  setDraft((d: CivilAppellateWizardDraft) => ({ ...d, isAppealMatter: 'yes' }));
                  clearErr('isAppealMatter');
                }}
              />
              <span>{f.yes}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="appealMatter"
                checked={draft.isAppealMatter === 'no'}
                onChange={() => {
                  setDraft((d: CivilAppellateWizardDraft) => ({
                    ...d,
                    isAppealMatter: 'no',
                    originalCourt: '',
                    judgmentOrOrderDocuments: [] }));
                  clearErr('isAppealMatter');
                  clearErr('originalCourt');
                  clearErr('judgmentOrOrderDocuments');
                }}
              />
              <span>{f.no}</span>
            </label>
          </div>
          <FieldError message={errors.isAppealMatter} />
        </div>

        {draft.isAppealMatter === 'yes' ? (
          <>
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                {f.originalCourt} <span className="req">*</span>
              </label>
              <input
                id="capl-originalCourt"
                className={inpClass(Boolean(errors.originalCourt))}
                type="text"
                value={draft.originalCourt}
                onChange={(e) => {
                  setDraft((d: CivilAppellateWizardDraft) => ({ ...d, originalCourt: maxLen('originalCourt', e.target.value, MAX_REF) }));
                }}
                placeholder={f.nameOfTheCourt}
              />
              <FieldError message={errors.originalCourt} />
            </div>
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                {f.judgementOrder} <span className="req">*</span>
              </label>
              <WizardTempFileUploadList
                files={draft.judgmentOrOrderDocuments}
                uploadFile={civilAppellateService.uploadCaseFile}
                error={errors.judgmentOrOrderDocuments}
                onClearError={() => clearErr('judgmentOrOrderDocuments')}
                onChange={(judgmentOrOrderDocuments) =>
                  setDraft((d: CivilAppellateWizardDraft) => ({ ...d, judgmentOrOrderDocuments }))
                }
                addButtonLabel={f.uploadDocuments}
              />
            </div>
          </>
        ) : null}

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.province} <span className="req">*</span>
            </label>
            <WizardSelect
              id="capl-province"
              hasError={Boolean(errors.provinceId)}
              value={draft.provinceId}
              placeholder={f.selectProvince}
              options={toOpts(lookups.provinces)}
              onChange={(id) => {
                setDraft((d: CivilAppellateWizardDraft) => ({
                  ...d,
                  provinceId: id,
                  provinceName: lookupName(lookups.provinces, id),
                  districtId: '',
                  districtName: '' }));
                clearErr('provinceId');
              }}
            />
            <FieldError message={errors.provinceId} />
          </div>
          <div className="ccr-fg">
            <label>
              {f.district} <span className="req">*</span>
            </label>
            <WizardSelect
              id="capl-district"
              hasError={Boolean(errors.districtId)}
              value={draft.districtId}
              disabled={!draft.provinceId}
              placeholder={f.selectDistrict}
              options={toOpts(lookups.districts)}
              onChange={(id) => {
                setDraft((d: CivilAppellateWizardDraft) => ({
                  ...d,
                  districtId: id,
                  districtName: lookupName(lookups.districts, id) }));
                clearErr('districtId');
              }}
            />
            <FieldError message={errors.districtId} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.yourReferenceNumber} <span className="req">*</span>
            </label>
            <input
              id="capl-yourRef"
              className={inpClass(Boolean(errors.yourReferenceNumber))}
              type="text"
              value={draft.yourReferenceNumber}
              onChange={(e) => {
                setDraft((d: CivilAppellateWizardDraft) => ({ ...d, yourReferenceNumber: maxLen('yourReferenceNumber', e.target.value, MAX_REF) }));
              }}
              placeholder={f.enterExternalReferenceNumber}
            />
            <FieldError message={errors.yourReferenceNumber} />
          </div>
          <div className="ccr-fg">
            <label>{f.otherExternalReferences}</label>
            <input
              className={inpClass(Boolean(errors.otherExternalReferences))}
              type="text"
              value={draft.otherExternalReferences}
              onChange={(e) =>
                setDraft((d: CivilAppellateWizardDraft) => ({ ...d, otherExternalReferences: maxLen('otherExternalReferences', e.target.value, MAX_REF) }))
              }
              placeholder={f.commaSeparatedReferenceNumbers}
            />
            <FieldError message={errors.otherExternalReferences} />
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.caseNumber}</label>
            <input
              className={inpClass(Boolean(errors.caseNumber))}
              type="text"
              value={draft.caseNumber}
              onChange={(e) =>
                setDraft((d: CivilAppellateWizardDraft) => ({ ...d, caseNumber: maxLen('caseNumber', e.target.value, MAX_REF) }))
              }
              placeholder={f.courtReferenceNumber}
            />
            <FieldError message={errors.caseNumber} />
          </div>
          <div className="ccr-fg" id="capl-connected">
            <label>
              {f.connectedMatters} <span className="req">*</span>
            </label>
            <div className="ccr-radio-row">
              <label className="ccr-radio">
                <input
                  type="radio"
                  name="connectedMatters"
                  checked={draft.hasConnectedMatters === 'yes'}
                  onChange={() => {
                    setDraft((d: CivilAppellateWizardDraft) => ({ ...d, hasConnectedMatters: 'yes' }));
                    clearErr('hasConnectedMatters');
                  }}
                />
                <span>{f.yes}</span>
              </label>
              <label className="ccr-radio">
                <input
                  type="radio"
                  name="connectedMatters"
                  checked={draft.hasConnectedMatters === 'no'}
                  onChange={() => {
                    setDraft((d: CivilAppellateWizardDraft) => ({
                      ...d,
                      hasConnectedMatters: 'no',
                      connectedCaseNumber: '',
                      connectedAgdFileRef: '' }));
                    clearErr('hasConnectedMatters');
                  }}
                />
                <span>{f.no}</span>
              </label>
            </div>
            <FieldError message={errors.hasConnectedMatters} />
          </div>
        </div>

        {draft.hasConnectedMatters === 'yes' ? (
          <>
            <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
              <div className="ccr-fg" style={{ marginBottom: 0 }}>
                <label>
                  {f.connectedCaseNumber} <span className="req">*</span>
                </label>
              </div>
              <div className="ccr-fg" style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ marginBottom: 0 }}>
                    {f.connectedAgdFileRef} <span className="req">*</span>
                  </label>
                  <input
                    type="checkbox"
                    style={{ width: 15, height: 15, accentColor: '#000', cursor: 'pointer', flexShrink: 0 }}
                    checked={draft.connectedAgdFileRefAvailable}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setDraft((d: CivilAppellateWizardDraft) => ({
                        ...d,
                        connectedAgdFileRefAvailable: checked,
                        connectedAgdFileRef: checked ? d.connectedAgdFileRef : '',
                      }));
                      clearErr('connectedAgdFileRef');
                    }}
                  />
                  <span>Available</span>
                </div>
              </div>
            </div>

            <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
              <div className="ccr-fg">
                <input
                  id="capl-connectedCase"
                  className={inpClass(Boolean(errors.connectedCaseNumber))}
                  type="text"
                  maxLength={MAX_REF}
                  value={draft.connectedCaseNumber}
                  onChange={(e) => {
                    setDraft((d: CivilAppellateWizardDraft) => ({ ...d, connectedCaseNumber: sliceMax(e.target.value, MAX_REF) }));
                    clearErr('connectedCaseNumber');
                  }}
                  placeholder="Enter Connected Case Number"
                />
                <FieldError message={errors.connectedCaseNumber} />
              </div>
              <div className="ccr-fg">
                <input
                  id="capl-connectedAgd"
                  className={inpClass(Boolean(errors.connectedAgdFileRef))}
                  type="text"
                  maxLength={MAX_REF}
                  disabled={!draft.connectedAgdFileRefAvailable}
                  style={!draft.connectedAgdFileRefAvailable ? { cursor: 'default', pointerEvents: 'none' } : undefined}
                  value={draft.connectedAgdFileRef}
                  onChange={(e) => {
                    setDraft((d: CivilAppellateWizardDraft) => ({ ...d, connectedAgdFileRef: sliceMax(e.target.value, MAX_REF) }));
                    clearErr('connectedAgdFileRef');
                  }}
                  placeholder="Enter AGD Reference Number"
                />
                <div className="ccr-slbl">
                  If you are unaware of the relevant Connected AGD File Reference Number details, please leave the
                  'Available' checkbox unchecked.
                </div>
                <FieldError message={errors.connectedAgdFileRef} />
              </div>
            </div>
          </>
        ) : null}

      </SectionCard>

      <CivilAppellateWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={1}
        onNext={next}
        validateBeforeSave={validate}
      />
    </CivilAppellateWizardShell>
  );
}
